import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Button,
  Dropdown,
  Input,
  Modal,
  Switch,
  Tag,
  message,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Locale } from "../../../utils";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import { BreadCrumb } from "../../../components/main/LayoutComponents";
import {
  createEmptyTemplate,
  createComponent,
  createRow,
} from "./catalog";
import {
  listTemplates,
  deleteTemplate,
  duplicateTemplate,
  saveTemplate,
  setTemplateFlag,
  buildDefaultA4Template,
  buildDefault80Template,
} from "./storage";
import "./PrintTemplateList.scss";

const i18n = Locale.getInstance();

function formatTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const p = (n) => (n < 10 ? `0${n}` : String(n));
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()} ${p(
      d.getHours()
    )}:${p(d.getMinutes())}`;
  } catch {
    return iso;
  }
}

export default function PrintTemplateList() {
  const history = useHistory();
  const user = useSelector((state) => getUserInfo(state));
  const merchantId = user?.merchant?.id;
  const [query, setQuery] = useState("");
  const [desktopNew, setDesktopNew] = useState(true);
  const [mobileNew, setMobileNew] = useState(true);
  const [tick, setTick] = useState(0);

  const templates = useMemo(() => {
    void tick;
    return listTemplates(merchantId);
  }, [merchantId, tick]);

  const filtered = templates.filter((t) =>
    (t.name || "").toLowerCase().includes(query.trim().toLowerCase())
  );

  const refresh = () => setTick((n) => n + 1);

  const goEdit = (id) =>
    history.push(`/main/order/print-templates/edit/${id}`);

  const onCreate = (paperSize) => {
    const tpl =
      paperSize === "80mm"
        ? buildDefault80Template()
        : buildDefaultA4Template();
    tpl.id = createEmptyTemplate().id;
    tpl.name =
      paperSize === "80mm"
        ? i18n.t("order.printTemplate.default80Name")
        : i18n.t("order.printTemplate.defaultA4Name");
    tpl.isDesktopDefault = false;
    tpl.isMobileDefault = false;
    // empty-ish start for "blank" feel but keep structure
    if (paperSize === "A4") {
      const title = createComponent({
        type: "custom-text",
        labelKey: "order.printTemplate.comps.customText",
      });
      title.text = i18n.t("order.printTemplate.salesOrder");
      title.align = "center";
      title.bold = true;
      title.fontSize = 16;
      tpl.layout.rows = [createRow([title])];
    }
    saveTemplate(merchantId, tpl);
    refresh();
    goEdit(tpl.id);
  };

  const createMenu = {
    items: [
      {
        key: "a4",
        label: i18n.t("order.printTemplate.createA4"),
        onClick: () => onCreate("A4"),
      },
      {
        key: "80",
        label: i18n.t("order.printTemplate.create80"),
        onClick: () => onCreate("80mm"),
      },
    ],
  };

  return (
    <div className="print-template-list">
      <BreadCrumb
        data={[
          {
            title: i18n.t("main.sideMenus.order"),
            link: "/main/order/list",
          },
          { title: i18n.t("main.sideMenus.printTemplates") },
        ]}
      />

      <div className="print-template-list__toolbar">
        <Input.Search
          allowClear
          placeholder={i18n.t("order.printTemplate.search")}
          style={{ maxWidth: 280 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="print-template-list__toolbar-right">
          <label className="print-template-list__switch">
            <span>{i18n.t("order.printTemplate.desktopNewPrint")}</span>
            <Switch checked={desktopNew} onChange={setDesktopNew} />
          </label>
          <label className="print-template-list__switch">
            <span>{i18n.t("order.printTemplate.mobileNewPrint")}</span>
            <Switch checked={mobileNew} onChange={setMobileNew} />
          </label>
          <Dropdown menu={createMenu} placement="bottomRight">
            <Button type="primary" icon={<PlusOutlined />}>
              {i18n.t("order.printTemplate.create")}
            </Button>
          </Dropdown>
        </div>
      </div>

      <div className="print-template-list__section-head">
        <div>
          <h2>
            {i18n.t("order.printTemplate.salesOrderTemplates")} ({filtered.length})
          </h2>
          <p className="print-template-list__hint">
            {desktopNew && mobileNew
              ? i18n.t("order.printTemplate.usingNewBoth")
              : i18n.t("order.printTemplate.manageHint")}
          </p>
        </div>
        <span className="print-template-list__count">
          {filtered.length} {i18n.t("order.printTemplate.templatesUnit")}
        </span>
      </div>

      <div className="print-template-list__grid">
        {filtered.map((tpl) => (
          <div
            key={tpl.id}
            className={`print-template-card ${
              tpl.isDesktopDefault ? "is-default" : ""
            }`}
          >
            <div
              className="print-template-card__preview"
              onClick={() => goEdit(tpl.id)}
            >
              <div className="print-template-card__doc">
                <FileTextOutlined />
                <span>
                  {tpl.paperSize === "A4"
                    ? i18n.t("order.printTemplate.a4Custom")
                    : i18n.t("order.printTemplate.mmBasic", {
                        size: tpl.paperSize,
                      })}
                </span>
              </div>
            </div>
            <div className="print-template-card__body">
              <div className="print-template-card__title" title={tpl.name}>
                {tpl.name}
              </div>
              <div className="print-template-card__meta">
                <span>
                  {i18n.t("order.printTemplate.updated")}:{" "}
                  {formatTime(tpl.updatedAt)}
                </span>
                <Tag>{tpl.copies || 1} {i18n.t("order.printTemplate.copies")}</Tag>
                <Tag>
                  {tpl.lang === "ug"
                    ? i18n.t("order.printTemplate.langUg")
                    : i18n.t("order.printTemplate.langZh")}
                </Tag>
                {tpl.isDesktopDefault ? (
                  <Tag color="blue">{i18n.t("order.printTemplate.desktopDefault")}</Tag>
                ) : null}
              </div>
            </div>
            <div className="print-template-card__actions">
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                onClick={() => {
                  Modal.info({
                    title: i18n.t("order.printTemplate.settings"),
                    content: (
                      <div className="print-template-settings">
                        <label>
                          <span>{i18n.t("order.printTemplate.desktopEnabled")}</span>
                          <Switch
                            defaultChecked={!!tpl.desktopEnabled}
                            onChange={(v) => {
                              setTemplateFlag(merchantId, tpl.id, {
                                desktopEnabled: v,
                              });
                              refresh();
                            }}
                          />
                        </label>
                        <label>
                          <span>{i18n.t("order.printTemplate.mobileEnabled")}</span>
                          <Switch
                            defaultChecked={!!tpl.mobileEnabled}
                            onChange={(v) => {
                              setTemplateFlag(merchantId, tpl.id, {
                                mobileEnabled: v,
                              });
                              refresh();
                            }}
                          />
                        </label>
                        <label>
                          <span>{i18n.t("order.printTemplate.setDesktopDefault")}</span>
                          <Switch
                            defaultChecked={!!tpl.isDesktopDefault}
                            onChange={(v) => {
                              setTemplateFlag(merchantId, tpl.id, {
                                isDesktopDefault: v,
                              });
                              refresh();
                            }}
                          />
                        </label>
                      </div>
                    ),
                  });
                }}
              >
                {i18n.t("order.printTemplate.settings")}
              </Button>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => goEdit(tpl.id)}
              >
                {i18n.t("order.printTemplate.design")}
              </Button>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  duplicateTemplate(merchantId, tpl.id);
                  message.success(i18n.t("order.printTemplate.duplicated"));
                  refresh();
                }}
              >
                {i18n.t("order.printTemplate.copy")}
              </Button>
              <Button
                type="text"
                size="small"
                icon={<CheckCircleOutlined />}
                className={tpl.desktopEnabled ? "is-on" : ""}
                onClick={() => {
                  setTemplateFlag(merchantId, tpl.id, {
                    desktopEnabled: !tpl.desktopEnabled,
                  });
                  refresh();
                }}
              >
                {i18n.t("order.printTemplate.desktopEnabled")}
              </Button>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: i18n.t("order.printTemplate.deleteConfirm"),
                    onOk: () => {
                      deleteTemplate(merchantId, tpl.id);
                      message.success(i18n.t("order.printTemplate.deleted"));
                      refresh();
                    },
                  });
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
